'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type Role = 'student' | 'instructor' | 'shs_student' | 'professional';
type AuthTab = 'login' | 'signup' | 'forgot';

interface LoginForm { email: string; password: string; }
interface SignupForm { fullName: string; email: string; password: string; role: Role; terms: boolean; }

const roleImages: Record<Role, { src: string; alt: string }> = {
  shs_student: {
    src: '/assets/images/shs_students.png',
    alt: 'African SHS students studying in a Ghanaian classroom',
  },
  student: {
    src: '/assets/images/tertiary_students.png',
    alt: 'African university students on campus with laptops',
  },
  professional: {
    src: '/assets/images/professional_learner.png',
    alt: 'African professional working in a modern office environment',
  },
  instructor: {
    src: '/assets/images/instructor_lecturer.png',
    alt: 'African lecturer teaching in a university classroom',
  },
};

export default function AuthScreen() {
  const [tab, setTab] = useState<AuthTab>('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role>('student');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [loginData, setLoginData] = useState<LoginForm>({ email: '', password: '' });
  const [signupData, setSignupData] = useState<SignupForm>({ fullName: '', email: '', password: '', role: 'student', terms: false });
  const [forgotEmail, setForgotEmail] = useState('');
  const [loginErrors, setLoginErrors] = useState<Partial<LoginForm>>({});
  const [signupErrors, setSignupErrors] = useState<Partial<SignupForm & { terms: string }>>({});
  // Unverified account state
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [unverifiedUserId, setUnverifiedUserId] = useState('');
  const [resendingCode, setResendingCode] = useState(false);
  const router = useRouter();
  const { signIn, signUp, sendVerificationCode } = useAuth();

  const getPasswordStrength = (pw: string) => {
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  };

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'];

  const getRoleRoute = (role: string) => {
    if (role === 'admin') return '/admin-dashboard';
    if (role === 'instructor') return '/instructor-dashboard';
    if (role === 'shs_student') return '/shs-dashboard';
    return '/student-dashboard';
  };

  const validateLogin = () => {
    const errors: Partial<LoginForm> = {};
    if (!loginData.email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginData.email)) errors.email = 'Enter a valid email';
    if (!loginData.password) errors.password = 'Password is required';
    setLoginErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSignup = () => {
    const errors: any = {};
    if (!signupData.fullName.trim()) errors.fullName = 'Full name is required';
    if (!signupData.email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) errors.email = 'Enter a valid email';
    if (!signupData.password || signupData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    if (!signupData.terms) errors.terms = 'You must accept the terms';
    setSignupErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLogin()) return;
    // Clear any previous unverified state
    setUnverifiedEmail('');
    setUnverifiedUserId('');
    setLoading(true);
    try {
      const result = await signIn(loginData.email, loginData.password);
      const supabase = createClient();

      // Determine role: check auth metadata first (reliable for admin),
      // then fall back to user_profiles
      const metaRole =
        result?.user?.user_metadata?.role ||
        result?.user?.app_metadata?.role ||
        null;

      let role = metaRole;
      let isShs = result?.user?.user_metadata?.is_shs_student || false;

      if (!role || role === 'student') {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role, is_shs_student')
          .eq('id', result?.user?.id)
          .maybeSingle();
        role = profile?.role || metaRole || 'student';
        isShs = profile?.is_shs_student || isShs || false;
      }

      toast.success('Welcome back! Redirecting to your dashboard...');
      if (role === 'admin') router.push('/admin-dashboard');
      else if (role === 'instructor') router.push('/instructor-dashboard');
      else if (isShs) router.push('/shs-dashboard');
      else router.push('/student-dashboard');
    } catch (error: any) {
      if (error?.message === 'email_not_verified') {
        setUnverifiedEmail(loginData.email);
        setUnverifiedUserId(error?.userId || '');
        router.push(`/verify-email?email=${encodeURIComponent(loginData.email)}&uid=${error?.userId || ''}`);
      } else {
        toast.error(error?.message || 'Invalid credentials. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendFromLogin = async () => {
    if (!unverifiedEmail || resendingCode) return;
    setResendingCode(true);
    try {
      // We need to get the user ID from the DB since they're not signed in
      const supabase = createClient();
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('id, full_name')
        .eq('email', unverifiedEmail)
        .maybeSingle();

      if (!profile?.id) {
        toast.error('Account not found. Please register first.');
        setResendingCode(false);
        return;
      }

      await sendVerificationCode(profile.id, unverifiedEmail, profile.full_name || unverifiedEmail.split('@')[0]);
      toast.success('Verification code sent! Check your email.');
      router.push(`/verify-email?email=${encodeURIComponent(unverifiedEmail)}&uid=${profile.id}`);
    } catch (error: any) {
      if (error?.message?.includes('rate_limit')) {
        toast.error('Please wait before requesting another code.');
      } else {
        toast.error('Failed to send verification code. Please try again.');
      }
    } finally {
      setResendingCode(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSignup()) return;
    setLoading(true);
    try {
      const result = await signUp(signupData.email, signupData.password, {
        fullName: signupData.fullName,
        role: signupData.role === 'shs_student' ? 'student' : signupData.role === 'professional' ? 'student' : signupData.role,
        is_shs_student: signupData.role === 'shs_student',
        is_professional: signupData.role === 'professional',
      });

      toast.success('Account created! A verification code has been sent to your email.');
      // Redirect to verification screen
      const userId = result?.user?.id || '';
      router.push(`/verify-email?email=${encodeURIComponent(signupData.email)}&uid=${userId}`);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
      });
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
      setTab('login');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const currentRoleImage = roleImages[selectedRole] || roleImages.student;

  return (
    <div className="min-h-screen flex">
      {/* Left image panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col">
        {/* Background image */}
        <div className="absolute inset-0">
          <Image
            src={tab === 'signup' ? currentRoleImage.src : '/assets/images/login_education.png'}
            alt={tab === 'signup' ? currentRoleImage.alt : 'African students in a modern educational environment'}
            fill
            className="object-cover transition-all duration-500"
            priority
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        </div>

        {/* Logo top-left */}
        <div className="relative z-10 p-10">
          <Link href="/" className="flex items-center">
            <AppLogo src="/assets/images/88677115-9FA9-456F-83F8-0F9B0A4E39DD-1778605171623.png" size={268} />
          </Link>
        </div>

        {/* Bottom text overlay */}
        <div className="relative z-10 mt-auto p-10">
          <h2 className="text-3xl font-extrabold text-white leading-tight mb-3">
            {tab === 'signup'
              ? selectedRole === 'shs_student' ? 'Excel in Your WAEC Exams'
              : selectedRole === 'professional' ? 'Advance Your Career'
              : selectedRole === 'instructor'? 'Share Your Expertise' : 'Unlock Your Academic Potential' :'Your learning journey starts here.'}
          </h2>
          <p className="text-white/80 text-base leading-relaxed mb-6">
            Join 24,800+ students and professionals unlocking world-class education on Nerdlora.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Courses Available', value: '380+', icon: 'BookOpenIcon' },
              { label: 'Certificates Issued', value: '4,200+', icon: 'TrophyIcon' },
              { label: 'Live Classes/Week', value: '140+', icon: 'VideoCameraIcon' },
              { label: 'Completion Rate', value: '91%', icon: 'CheckBadgeIcon' },
            ].map((s) => (
              <div key={`auth-stat-${s.label}`} className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <Icon name={s.icon as never} size={20} className="text-white mb-2" />
                <p className="text-xl font-extrabold text-white tabular-nums">{s.value}</p>
                <p className="text-xs text-white/70">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-12 lg:px-16 xl:px-24 overflow-y-auto bg-background">
        <div className="max-w-md w-full mx-auto">
          <Link href="/" className="flex items-center mb-8 lg:hidden">
            <AppLogo src="/assets/images/88677115-9FA9-456F-83F8-0F9B0A4E39DD-1778605171623.png" size={192} />
          </Link>

          {tab !== 'forgot' && (
            <div className="flex bg-muted rounded-xl p-1 mb-8">
              {(['login', 'signup'] as AuthTab[]).map((t) => (
                <button key={`tab-${t}`} onClick={() => { setTab(t); setUnverifiedEmail(''); setUnverifiedUserId(''); }}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${tab === t ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  {t === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>
          )}

          {/* LOGIN FORM */}
          {tab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <h1 className="text-2xl font-extrabold text-foreground mb-1">Welcome back</h1>
                <p className="text-sm text-muted-foreground">Sign in to continue your learning journey</p>
              </div>

              {/* Unverified account banner */}
              {unverifiedEmail && (
                <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="ExclamationTriangleIcon" size={18} className="text-warning mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Email not verified</p>
                      <p className="text-xs text-muted-foreground">
                        Please verify your email address before continuing. Check your inbox for the verification code.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleResendFromLogin}
                      disabled={resendingCode}
                      className="flex-1 py-2 px-3 bg-warning text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      {resendingCode ? (
                        <><span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</>
                      ) : (
                        <><Icon name="EnvelopeIcon" size={12} />Send Verification Code</>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => router.push(`/verify-email?email=${encodeURIComponent(unverifiedEmail)}&uid=${unverifiedUserId}`)}
                      className="flex-1 py-2 px-3 border border-border rounded-lg text-xs font-semibold text-foreground hover:bg-muted transition-all"
                    >
                      Enter Code
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Email address</label>
                <input type="email" value={loginData.email} onChange={(e) => setLoginData(p => ({ ...p, email: e.target.value }))}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
                {loginErrors.email && <p className="text-xs text-danger">{loginErrors.email}</p>}
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-semibold text-foreground">Password</label>
                  <button type="button" onClick={() => setTab('forgot')} className="text-xs text-primary hover:underline">Forgot password?</button>
                </div>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={loginData.password} onChange={(e) => setLoginData(p => ({ ...p, password: e.target.value }))}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 pr-10 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                  </button>
                </div>
                {loginErrors.password && <p className="text-xs text-danger">{loginErrors.password}</p>}
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3.5 gradient-purple text-white rounded-xl text-sm font-bold hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing in...</> : 'Sign In'}
              </button>
              <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{' '}
                <button type="button" onClick={() => setTab('signup')} className="text-primary font-semibold hover:underline">Create one</button>
              </p>
            </form>
          )}

          {/* SIGNUP FORM */}
          {tab === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-5">
              <div>
                <h1 className="text-2xl font-extrabold text-foreground mb-1">Create your account</h1>
                <p className="text-sm text-muted-foreground">Join thousands of learners on Nerdlora</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Full Name</label>
                <input type="text" value={signupData.fullName} onChange={(e) => setSignupData(p => ({ ...p, fullName: e.target.value }))}
                  placeholder="Kwame Mensah"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
                {signupErrors.fullName && <p className="text-xs text-danger">{signupErrors.fullName}</p>}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Email address</label>
                <input type="email" value={signupData.email} onChange={(e) => setSignupData(p => ({ ...p, email: e.target.value }))}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
                {signupErrors.email && <p className="text-xs text-danger">{signupErrors.email}</p>}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={signupData.password}
                    onChange={(e) => { setSignupData(p => ({ ...p, password: e.target.value })); setPasswordStrength(getPasswordStrength(e.target.value)); }}
                    placeholder="Create a strong password"
                    className="w-full px-4 py-3 pr-10 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                  </button>
                </div>
                {signupData.password && (
                  <div className="flex gap-1 mt-1.5">
                    {[1,2,3,4].map(i => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= passwordStrength ? strengthColor[passwordStrength] : 'bg-muted'}`} />
                    ))}
                    <span className="text-xs text-muted-foreground ml-2">{strengthLabel[passwordStrength]}</span>
                  </div>
                )}
                {signupErrors.password && <p className="text-xs text-danger">{signupErrors.password}</p>}
              </div>

              {/* Role Selection */}
              <div className="space-y-2 mb-2">
                <label className="text-sm font-semibold text-foreground">I am a...</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'shs_student', label: 'SHS Student', icon: 'AcademicCapIcon', desc: 'WAEC & SHS prep' },
                    { value: 'student', label: 'Tertiary Student', icon: 'BuildingLibraryIcon', desc: 'University & diploma' },
                    { value: 'professional', label: 'Professional', icon: 'BriefcaseIcon', desc: 'Career development' },
                    { value: 'instructor', label: 'Instructor', icon: 'UserGroupIcon', desc: 'Teach on Nerdlora' },
                  ].map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() => {
                        setSelectedRole(role.value as Role);
                        setSignupData(p => ({ ...p, role: role.value as Role }));
                      }}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-center ${selectedRole === role.value ? 'border-primary bg-secondary' : 'border-border hover:border-primary/40'}`}
                    >
                      <Icon name={role.icon as never} size={20} className={selectedRole === role.value ? 'text-primary' : 'text-muted-foreground'} />
                      <span className={`text-xs font-bold leading-tight ${selectedRole === role.value ? 'text-primary' : 'text-foreground'}`}>{role.label}</span>
                      <span className="text-xs text-muted-foreground leading-tight">{role.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <input type="checkbox" id="terms" checked={signupData.terms} onChange={(e) => setSignupData(p => ({ ...p, terms: e.target.checked }))}
                  className="w-4 h-4 mt-0.5 rounded border-border accent-primary flex-shrink-0" />
                <label htmlFor="terms" className="text-sm text-muted-foreground">
                  I agree to the{' '}
                  <Link href="/" className="text-primary hover:underline">Terms of Service</Link>{' '}and{' '}
                  <Link href="/" className="text-primary hover:underline">Privacy Policy</Link>
                </label>
              </div>
              {signupErrors.terms && <p className="text-xs text-danger">{signupErrors.terms}</p>}
              <button type="submit" disabled={loading}
                className="w-full py-3.5 gradient-purple text-white rounded-xl text-sm font-bold hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating account...</> : 'Create Account'}
              </button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button type="button" onClick={() => setTab('login')} className="text-primary font-semibold hover:underline">Sign in</button>
              </p>
            </form>
          )}

          {/* FORGOT PASSWORD FORM */}
          {tab === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <button type="button" onClick={() => setTab('login')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
                  <Icon name="ArrowLeftIcon" size={16} /> Back to sign in
                </button>
                <h1 className="text-2xl font-extrabold text-foreground mb-1">Reset your password</h1>
                <p className="text-sm text-muted-foreground">Enter your email and we will send you a reset link</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Email address</label>
                <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3.5 gradient-purple text-white rounded-xl text-sm font-bold hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</> : 'Send Reset Email'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}